import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { getFreshUserAccessToken, getLocalSession, safeRefresh } from '@/lib/accessTokenRefresh'
import { supabase } from '@/lib/supabase'
import { getValidatedAuthRedirect } from '@/lib/authRedirects'
import { createLogger } from '@/lib/logger'
import { messageFromFunctionsInvokeError } from '@/lib/edgeFunctionInvoke'

const log = createLogger('auth')

interface AuthContextType {
    user: User | null
    session: Session | null
    loading: boolean
    signUp: (
        email: string,
        password: string,
        fullName: string,
        plan?: string,
    ) => Promise<{ error: AuthError | null; session: Session | null }>
    signIn: (email: string, password: string) => Promise<{ error: AuthError | null; user: User | null }>
    signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>
    signOut: () => Promise<void>
    refreshSession: () => Promise<void>
    resendVerificationEmail: (email: string) => Promise<{ error: AuthError | null }>
    sendPasswordResetEmail: (email: string) => Promise<{ error: AuthError | null }>
    resetPassword: (newPassword: string) => Promise<{ error: AuthError | null }>
    updateEmail: (newEmail: string) => Promise<{ error: AuthError | null }>
    signInWithGoogle: () => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [loading, setLoading] = useState(true)
    // Track whether onAuthStateChange has already initialized us so that
    // the getLocalSession() fallback doesn't fire a redundant state update and
    // trigger duplicate side-effects in child components.
    const authInitializedRef = useRef(false)

    useEffect(() => {
        // Listen for auth changes FIRST so the INITIAL_SESSION event can
        // set state before getLocalSession() resolves. This eliminates the race
        // where both fire and trigger duplicate useEffect-based side-effects.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            log.debug('event', event)

            authInitializedRef.current = true
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)

            if (event === 'TOKEN_REFRESHED') {
                log.debug('token refreshed')
            }

            if (event === 'SIGNED_OUT') {
                setSession(null)
                setUser(null)
            }
        })

        // Fallback: if onAuthStateChange hasn't fired within ~100 ms
        // (e.g. network error or older Supabase client), resolve loading state.
        getLocalSession()
            .then((session) => {
                if (authInitializedRef.current) return // already handled by onAuthStateChange
                setSession(session)
                setUser(session?.user ?? null)
                setLoading(false)
            })
            .catch((err) => {
                if (err?.name === 'AbortError') return
                log.error('getLocalSession failed', err)
                if (!authInitializedRef.current) setLoading(false)
            })

        return () => subscription.unsubscribe()
    }, [])

    // Auto-refresh session every 10 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            void getFreshUserAccessToken().then((t) => {
                if (t) log.debug('session keepalive tick')
            })
        }, 10 * 60 * 1000) // Check every 10 minutes

        return () => clearInterval(interval)
    }, [])

    const signUp = async (email: string, password: string, fullName: string, plan: string = 'starter') => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: fullName,
                    plan: plan
                },
                // Always main app (not tenant subdomain) — Supabase appends tokens
                emailRedirectTo: getValidatedAuthRedirect('auth-callback')
            }
        })
        if (!error && data?.user) {
            log.debug('signup OK; welcome email via DB trigger and/or send-welcome-email-safe', { email })
            // Fallback when session exists (e.g. email confirmation disabled). Idempotent RPC inside.
            if (data.session?.access_token) {
                void supabase.functions
                    .invoke('send-welcome-email-safe', {
                        headers: { Authorization: `Bearer ${data.session.access_token}` },
                    })
                    .then(async ({ error: fnErr, data: fnData }) => {
                        if (fnErr) {
                            log.warn(
                                'send-welcome-email-safe',
                                await messageFromFunctionsInvokeError(fnErr),
                            )
                        } else {
                            log.debug('send-welcome-email-safe result', fnData)
                        }
                    })
            }
        }
        return { error, session: data?.session ?? null }
    }

    const signIn = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        })
        return { error, user: data?.user ?? null }
    }

    const signInWithMagicLink = async (email: string) => {
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: getValidatedAuthRedirect('dashboard')
            }
        })
        return { error }
    }

    const signInWithGoogle = async () => {
        const redirectTo = getValidatedAuthRedirect('auth-callback')
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo,
                queryParams: { prompt: 'select_account' },
            },
        })
        return { error }
    }

    const signOut = async () => {
        await supabase.auth.signOut()
    }

    const refreshSession = async () => {
        const { data, error } = await safeRefresh()
        if (error) {
            console.error('Failed to refresh session:', error)
        } else if (data.session) {
            setSession(data.session)
            setUser(data.session.user)
        }
    }

    const resendVerificationEmail = async (email: string) => {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email: email,
            options: { emailRedirectTo: getValidatedAuthRedirect('auth-callback') },
        })
        return { error }
    }

    const sendPasswordResetEmail = async (email: string) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: getValidatedAuthRedirect('reset-password')
        })
        return { error }
    }

    const resetPassword = async (newPassword: string) => {
        const { error } = await supabase.auth.updateUser({
            password: newPassword
        });

        if (error) {
            return { error };
        }

        // Security: Invalidate all sessions after password change
        await supabase.auth.signOut({ scope: 'global' });

        return { error: null };
    };

    const updateEmail = async (newEmail: string) => {
        const emailRedirectTo = getValidatedAuthRedirect('auth-callback')
        const { error } = await supabase.auth.updateUser(
            { email: newEmail },
            { emailRedirectTo }
        )
        return { error }
    }

    return (
        <AuthContext.Provider value={{
            user,
            session,
            loading,
            signUp,
            signIn,
            signInWithMagicLink,
            signInWithGoogle,
            signOut,
            refreshSession,
            resendVerificationEmail,
            sendPasswordResetEmail,
            resetPassword,
            updateEmail
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

